/**
 * useCortexNodeMap — MS-01 (C4.3 binary)
 * Read-only aggregator for the canonical Cortex OS nodes, joining
 * `cortex_feature_flags` (config — binary enabled/disabled) with
 * `cortex_events` (runtime telemetry) over a configurable time window.
 *
 * C4.3 (2026-04): no more `mode` / `rollout_pct` — flags são binárias.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type NodeStatus = "green" | "yellow" | "gray";

export interface CortexNode {
  key: string;
  label: string;
  description: string;
  flagKeys: string[];
  eventTypes: string[];
}

export interface NodeRow {
  node: CortexNode;
  flagKey: string | null;
  enabled: boolean | null;
  events24h: number;
  lastEventAt: string | null;
  status: NodeStatus;
}

// 12 canonical Cortex nodes
export const CORTEX_NODES: CortexNode[] = [
  {
    key: "threat-detect",
    label: "Threat Detect",
    description: "Detecta ameaças e linguagem hostil no turno.",
    flagKeys: ["cortex_threat_detection"],
    eventTypes: ["threat.detected", "threat.scored"],
  },
  {
    key: "conflict-detect",
    label: "Conflict Detect",
    description: "Identifica conflito interpessoal no grupo.",
    flagKeys: ["cortex_conflict_detection"],
    eventTypes: ["conflict.detected", "conflict.scored"],
  },
  {
    key: "input-gating",
    label: "Input Gating",
    description: "Filtra ruído e decide se o turno entra no pipeline.",
    flagKeys: ["cortex_input_gating"],
    eventTypes: ["input.gated", "input.passed"],
  },
  {
    key: "pattern-separation",
    label: "Pattern Separation",
    description: "Distingue contextos parecidos para evitar mistura.",
    flagKeys: ["cortex.pattern_sep_enforced"],
    eventTypes: ["pattern.separated", "pattern_separation.run"],
  },
  {
    key: "tom-update",
    label: "ToM Update",
    description: "Theory of Mind — inferência de intenção e estado social.",
    flagKeys: ["cortex.tom.infer_intent_shadow", "cortex.tom.group_model", "cortex.tom.advanced_dims"],
    eventTypes: ["tom.updated", "tom.inferred"],
  },
  {
    key: "interoceptive-update",
    label: "Interoceptive Update",
    description: "Estado interno (energia, estresse, foco) da Syra.",
    flagKeys: ["cortex.interoceptive"],
    eventTypes: ["interoceptive.updated"],
  },
  {
    key: "group-state-classify",
    label: "Group State",
    description: "Classifica clima do grupo (calmo, agitado, brincando…).",
    flagKeys: ["cortex_full_loop_enabled"],
    eventTypes: ["group_state.classified", "group.state.updated"],
  },
  {
    key: "inhibitor-decide",
    label: "Inhibitor",
    description: "Decide se inibe a resposta apesar de qualificada.",
    flagKeys: ["cortex_inhibitor_enabled"],
    eventTypes: ["inhibitor.decided", "inhibitor.suppressed"],
  },
  {
    key: "learning-weight-update",
    label: "Learning Weights",
    description: "Atualiza pesos com base em feedback do turno.",
    flagKeys: ["feature.skill_metacog_v19"],
    eventTypes: ["learning.weight_updated"],
  },
  {
    key: "metacog-runtime",
    label: "Metacognition",
    description: "Reflexão sobre confiança e qualidade da resposta.",
    flagKeys: ["feature.skill_metacog_gate_v19", "feature.skill_metacog_v19"],
    eventTypes: ["metacog.evaluated", "metacog.run"],
  },
  {
    key: "memory-search",
    label: "Memory Search",
    description: "Retrieval semântico/keyword na memória consolidada.",
    flagKeys: ["cortex.ltm.v2_retrieval"],
    eventTypes: ["memory.searched", "memory_search.run"],
  },
  {
    key: "person-memory",
    label: "Person Memory",
    description: "Extrai e mantém fatos sobre pessoas do grupo.",
    flagKeys: ["feature.person_memory_v19"],
    eventTypes: ["person_memory.extracted", "person_memory.updated"],
  },
];

interface FlagRow {
  flag_key: string;
  enabled: boolean;
}

interface EventCount {
  event_type: string;
  occurred_at: string;
}

function statusFor(enabled: boolean | null, events24h: number): NodeStatus {
  if (enabled && events24h > 0) return "green";
  if (enabled && events24h === 0) return "yellow";
  return "gray";
}

export function useCortexNodeMap(windowHours: number = 24) {
  const [rows, setRows] = useState<NodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const allFlagKeys = Array.from(new Set(CORTEX_NODES.flatMap((n) => n.flagKeys)));
        const allEventTypes = Array.from(new Set(CORTEX_NODES.flatMap((n) => n.eventTypes)));
        const since = new Date(Date.now() - windowHours * 60 * 60 * 1000).toISOString();

        const [flagsRes, eventsRes] = await Promise.all([
          supabase
            .from("cortex_feature_flags")
            .select("flag_key, enabled")
            .in("flag_key", allFlagKeys),
          supabase
            .from("cortex_events")
            .select("event_type, occurred_at")
            .in("event_type", allEventTypes)
            .gte("occurred_at", since)
            .order("occurred_at", { ascending: false })
            .limit(5000),
        ]);

        if (flagsRes.error) throw flagsRes.error;
        if (eventsRes.error) throw eventsRes.error;
        if (cancelled) return;

        const flagMap = new Map<string, FlagRow>();
        for (const f of (flagsRes.data ?? []) as FlagRow[]) flagMap.set(f.flag_key, f);

        const eventsByType = new Map<string, EventCount[]>();
        for (const e of (eventsRes.data ?? []) as EventCount[]) {
          const arr = eventsByType.get(e.event_type) ?? [];
          arr.push(e);
          eventsByType.set(e.event_type, arr);
        }

        const computed: NodeRow[] = CORTEX_NODES.map((node) => {
          let chosen: FlagRow | null = null;
          for (const fk of node.flagKeys) {
            const f = flagMap.get(fk);
            if (f) { chosen = f; break; }
          }
          let events24h = 0;
          let lastEventAt: string | null = null;
          for (const et of node.eventTypes) {
            const arr = eventsByType.get(et) ?? [];
            events24h += arr.length;
            if (arr.length > 0) {
              const top = arr[0].occurred_at;
              if (!lastEventAt || top > lastEventAt) lastEventAt = top;
            }
          }
          const enabled = chosen?.enabled ?? null;
          return {
            node,
            flagKey: chosen?.flag_key ?? node.flagKeys[0] ?? null,
            enabled,
            events24h,
            lastEventAt,
            status: statusFor(enabled, events24h),
          };
        });

        setRows(computed);
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [windowHours]);

  return { rows, loading, error };
}
